import { EFinancingProgram, EPlanType, EWorksheetDecision } from '@if/core/types'
import { ErrorOutline } from '@mui/icons-material'
import { Grid, Stack, useTheme } from '@mui/material'
import { Breadcrumb, InputTextField, PageError, PageSpinner } from '@src/components'
import AvailableCreditTooltip from '@src/components/AvailableCreditTooltip'
import PendingMessage from '@src/components/PendingMessage'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import WarningMessageAccordion from '@src/containers/ViewCreditApplicationPage/components/warningMessageAccordion'
import { useCreditApplicationContext, useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useGetAvailableCredit } from '@src/data/api/available-credit-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import {
  useComputeIfXpressFunding,
  useUpdateIfXpressWorksheetDecision,
} from '@src/data/api/ifxpress-worksheet/ifxpress-worksheet-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { useDeleteMessage } from '@src/data/api/message/message-api'
import { useBusinessErrors, useFinancingConfig } from '@src/data/selectors/app-config-selectors'
import {
  useAreAllTasksRequiredForCVTCompleted,
  useConfirmAllIncomesStatus,
  useCurrentCreditApplicationSpecificNorms,
  useIsFinalDecisionPending,
} from '@src/data/selectors/credit-selectors'
import { useAreAllDocumentsRequiredForCVT } from '@src/data/selectors/document-selectors'
import {
  BaseWorksheetFundingThresholdExtend,
  CreditApplicationMessage,
  NormsMessage,
  WorksheetDecision,
} from '@src/data/types'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { IfXpressWorksheet, IfXpressWorksheetDecisionDTO } from '@src/data/types/IfXpressWorksheetSchema'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import translate from '@src/services/translate'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LoanSummaryBox from '../components/LoanSummary/LoanSummaryBox'
import WorksheetDecisionDialog from '../components/LoanSummary/WorksheetDecisionDialog'
import WorksheetDecisionSection from '../components/LoanSummary/WorksheetDecisionSection'
import TransactionSection from '../components/Transaction/TransactionSection'
import { useDialogDecisionDefaultValue, useViewTotalObligation } from '../viewWorksheet-hooks'
import { canComputeFunding, getEmptyComputedFundingDto } from '../viewWorksheet-selectors'

const IfXpressWorksheetViewPage = () => {
  const { t } = useTranslation()
  const theme = useTheme()

  const { creditApplication, worksheet, isLoading, privateMessages } = useCreditApplicationData()
  const ifXpressWorksheet = worksheet as IfXpressWorksheet | undefined

  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')

  const [computedInfo, setComputedInfo] = useState<FundingComputedDto>({} as FundingComputedDto)

  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()

  const [computeFunding] = useComputeIfXpressFunding()
  const [updateDecision] = useUpdateIfXpressWorksheetDecision()
  const [deleteMessage] = useDeleteMessage()
  const error = useBusinessErrors()
  const commonNormsMessage: NormsMessage[] = useCurrentCreditApplicationSpecificNorms()
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')
  const activePaymentPlan = merchant?.paymentPlans?.[0] ?? null
  const financingConfig = useFinancingConfig(creditApplication?.financingProgramId)

  const { id, financingProgramId } = useCreditApplicationContext()
  const [availableCreditData] = useGetAvailableCredit(financingProgramId, id)
  const availableCredit = availableCreditData?.availableAmount ?? 0
  const creditInProgress = availableCreditData?.creditInProgress ?? 0

  const totalObligation = useViewTotalObligation(ifXpressWorksheet?.amountRequested, computedInfo)

  const user = useUser()

  const worksheetNormWarningMessages = useMemo(() => {
    if (!ifXpressWorksheet?.normsDecision?.warningMessages) return []
    return ifXpressWorksheet.normsDecision.warningMessages.map((wr) => {
      const translatedMessage = translate([`worksheet.norms.${wr.id}`])
      let messageSuffix = ' | '
      wr.parameters.forEach((p, i) => {
        if (i >= 1) messageSuffix = messageSuffix.concat(', ')
        const suffix = translate(`worksheet.paramNames.${p.name}`, { val: p.value })
        messageSuffix = messageSuffix.concat(suffix)
      })
      return {
        id: wr.id,
        message: translatedMessage.concat(messageSuffix),
        applicantType: null,
      } as NormsMessage
    })
  }, [ifXpressWorksheet?.normsDecision?.warningMessages])

  const { documents, requiredDocuments } = useCreditApplicationData()
  const areAllTasksRequiredForCVTCompleted = useAreAllTasksRequiredForCVTCompleted()
  const areAllDocumentsRequiredForCVTApproved = useAreAllDocumentsRequiredForCVT(documents, requiredDocuments)
  const confirmAllIncomesStatus = useConfirmAllIncomesStatus()
  const finalDecisionIsPending = useIsFinalDecisionPending()

  const dialogDecisionDefaultValue = useDialogDecisionDefaultValue(
    creditApplication!,
    ifXpressWorksheet!,
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    confirmAllIncomesStatus!,
  )

  const editDisabled = creditApplication?.editLocked === true || finalDecisionIsPending
  const planName = creditApplication?.normsCreditDecision?.planName ?? EPlanType.Default

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.IfXpress}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const EditDecisionDialogOpen = 'EditDecisionDialogOpen'

  useEffect(() => {
    if (creditApplication && ifXpressWorksheet && Object.keys(computedInfo).length === 0) {
      const interestRate = creditApplication.finalCreditDecision.interestRate ?? 0
      const hasCoApplicant = creditApplication.coApplicant !== null
      const stateIso = creditApplication?.applicant.currentAddress.stateIso
      const requestId = nanoid()

      const computeDto: FundingInfoDto = {
        financingProgramId: creditApplication.financingProgramId,
        amountRequested: ifXpressWorksheet.amountRequested ?? 0,
        includeInsurance: ifXpressWorksheet.includeInsurance,
        paymentFrequency: ifXpressWorksheet.paymentFrequency,
        paymentPlanId: ifXpressWorksheet.paymentPlanId,
        term: ifXpressWorksheet.term,
        interestRate,
        hasCoApplicant,
        stateIso: stateIso,
        deliveryOn: ifXpressWorksheet.deliveryOn,
        firstPaymentOn: ifXpressWorksheet.firstPaymentOn,
        requestId,
        creditApplicationId: creditApplication.id,
        merchantId: creditApplication.merchantId,
        insuranceCompanyId: ifXpressWorksheet.insuranceCompanyId,
      }

      if (canComputeFunding(computeDto, financingConfig.minimumLoanAmount)) {
        const fetchFunding = async () => {
          try {
            const data = await computeFunding(computeDto)
            setComputedInfo(data)
          } catch (error) {
            reportErrorToConsole(error)
          }
        }
        void fetchFunding()
      } else {
        const emptyComputedDto = getEmptyComputedFundingDto()

        setComputedInfo(emptyComputedDto)
      }
    }
  }, [ifXpressWorksheet, computedInfo, creditApplication, financingConfig.minimumLoanAmount, computeFunding])

  // callbacks
  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [])

  const handleSaveDecision = useCallback(
    async (data: WorksheetDecision) => {
      if (ifXpressWorksheet) {
        const updatedFinalDecision: IfXpressWorksheetDecisionDTO = {
          creditApplicationId: ifXpressWorksheet.creditApplicationId,
          decision: data.decision,
          otherReason: data.otherReason,
          versionTag: ifXpressWorksheet.versionTag,
        } as IfXpressWorksheetDecisionDTO

        if (data.decision === EWorksheetDecision.ApprovedWithCondition) {
          const fundingThresholdExtend: BaseWorksheetFundingThresholdExtend = {
            maxPmtAmount: data.maxPmtAmount,
            maxTotalAmountFinanced: data.maxTotalAmountFinanced,
            maxTermDuration: data.maxTermDuration,
          }
          updatedFinalDecision.fundingThresholdExtend = fundingThresholdExtend
        }

        try {
          await updateDecision({
            ...updatedFinalDecision,
            creditApplicationId: creditApplication?.id,
          })
          closeDialogs()
          await invalidateFullCreditApplication(creditApplication!.id, creditApplication!.financingProgramId)
        } catch (error) {
          reportErrorToConsole(error)
        }
      }
    },
    [ifXpressWorksheet, creditApplication, closeDialogs, invalidateFullCreditApplication, updateDecision],
  )

  const handleDeletingPrivateMessage = async (message: CreditApplicationMessage) => {
    if (creditApplication?.id) {
      await deleteMessage({
        id: message.id,
        creditApplicationId: message.creditApplicationId,
        financingProgramId: creditApplication.financingProgramId,
      })
    }
  }

  return (
    <div>
      <PageSpinner isLoading={isLoading} />
      {!isLoading && ifXpressWorksheet && creditApplication && (
        <>
          <Breadcrumb trees={breadCrumbs} />
          <Stack>
            <PageError errors={error} />
            {finalDecisionIsPending && <PendingMessage />}
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <InputTextField
                value={t(`EPlanType.${planName}`, { ns: 'enum' })}
                label={t('worksheetCommon.program')}
                disabled
              />
              <InputTextField
                label={t('worksheetCommon.creditLimit')}
                disabled
                value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0}
              />
              <AvailableCreditTooltip availableCreditData={availableCreditData}>
                <div style={{ position: 'relative' }}>
                  <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
                  {creditInProgress !== 0 && (
                    <ErrorOutline
                      sx={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: theme.palette.error.main,
                        fontSize: '1.5rem',
                      }}
                    />
                  )}
                </div>
              </AvailableCreditTooltip>

              <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />
            </Stack>
            {commonNormsMessage?.length > 0 && (
              <WarningMessageAccordion
                applicantCreditWarnings={null}
                coapplicantCreditWarnings={null}
                commonCreditWarnings={commonNormsMessage}
                worksheetWarnings={null}
                expanded={false}
              />
            )}

            <PrivateMessage
              messages={privateMessages}
              handleDeletingPrivateMessage={handleDeletingPrivateMessage}
              currentUser={user}
              creditAppCreationDate={creditApplication.createdOn}
            />
            <Grid container textAlign="center" justifyContent="space-around" p={4}>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <LoanSummaryBox
                  worksheet={ifXpressWorksheet}
                  computedInfo={computedInfo}
                  merchantPaymentPlan={activePaymentPlan ?? null}
                />
              </Grid>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <WorksheetDecisionSection
                  worksheet={ifXpressWorksheet}
                  onClick={() => setCurrentOpenDialog(EditDecisionDialogOpen)}
                  editDisabled={editDisabled}
                  userCanEditWorksheet={user?.rights.canEditWorksheet === true}
                />
              </Grid>
            </Grid>

            <TransactionSection
              worksheet={ifXpressWorksheet}
              creditApplication={creditApplication}
              totalObligation={totalObligation}
              merchantPaymentPlan={activePaymentPlan ?? null}
              productsPlans={[]}
            />
          </Stack>

          <WorksheetDecisionDialog
            open={currentOpenDialog === EditDecisionDialogOpen}
            title={t('worksheet.decision')}
            onCancel={closeDialogs}
            onConfirm={handleSaveDecision}
            worksheetWarnings={worksheetNormWarningMessages}
            defaultValue={dialogDecisionDefaultValue}
            worksheet={ifXpressWorksheet}
            creditapplication={creditApplication}
          />
        </>
      )}
    </div>
  )
}

export default IfXpressWorksheetViewPage
